import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { ROUTES, generatePath } from '@/constants/routes'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { BookOpen, Loader2, Plus } from "lucide-react"
import { listCourses, type CourseListItem } from "../../api/aiTeam"

export default function CoursesPage() {
  const [courses, setCourses] = useState<CourseListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>("all")

  useEffect(() => {
    loadCourses()
  }, [])

  async function loadCourses() {
    try {
      const data = await listCourses()
      setCourses(data)
    } catch (err) {
      console.error("Failed to load courses:", err)
    } finally {
      setLoading(false)
    }
  }

  const filteredCourses =
    filter === "all"
      ? courses
      : courses.filter((c) => c.status === filter)

  const courseTypeLabels: Record<string, string> = {
    preview: "预习课",
    new: "新授课",
    review: "复习课",
    exam_prep: "备考课",
    targeted: "专项课",
  }

  const statusLabels: Record<string, string> = {
    planning: "规划中",
    generating: "生成中",
    ready: "待开始",
    in_progress: "进行中",
    completed: "已完成",
  }

  const statusColors: Record<string, string> = {
    planning: "bg-slate-100 text-slate-600",
    generating: "bg-blue-100 text-blue-600",
    ready: "bg-yellow-100 text-yellow-600",
    in_progress: "bg-green-100 text-green-600",
    completed: "bg-slate-100 text-slate-500",
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">我的课程</h1>
          <p className="text-slate-500 mt-1">管理你的学习课程</p>
        </div>
        <Link to={ROUTES.AI_TEAM_NEW_COURSE}>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            新建课程
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {["all", "in_progress", "ready", "completed"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f === "all"
              ? "全部"
              : f === "in_progress"
              ? "进行中"
              : f === "ready"
              ? "待开始"
              : "已完成"}
          </Button>
        ))}
      </div>

      {filteredCourses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCourses.map((course) => (
            <Link key={course.id} to={generatePath(ROUTES.AI_TEAM_COURSE_DETAIL, { courseId: course.id })}>
              <Card className="h-full hover:shadow-lg transition-all cursor-pointer">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{course.title}</CardTitle>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        statusColors[course.status] ?? statusColors.ready
                      }`}
                    >
                      {statusLabels[course.status] ?? course.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <BookOpen className="w-4 h-4" />
                      <span>{courseTypeLabels[course.course_type] ?? course.course_type}</span>
                      <span>·</span>
                      <span>{course.subject}</span>
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-600">学习进度</span>
                        <span className="font-medium">
                          {course.completed_lessons}/{course.total_lessons}节
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{
                            width: `${
                              course.total_lessons > 0
                                ? (course.completed_lessons / course.total_lessons) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">
                {filter === "all" ? "还没有课程" : "没有匹配的课程"}
              </h3>
              <p className="text-slate-500 mb-6">
                点击"新建课程"按钮，AI 将为你设计个性化课程
              </p>
              <Link to={ROUTES.AI_TEAM_NEW_COURSE}>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  新建课程
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
